/**
 * Hybrid voxel grid for fast ray-traced ambient occlusion.
 * Uses DDA for fast empty-space traversal + precise AABB intersection for accuracy.
 * Supports any axis-aligned cuboid regardless of position/size.
 */
export default class AOVoxelGrid {
  // Static ray cache: "nx,ny,nz|numRays" -> rays[]
  // Only 6 discrete face normals exist, so this caches all ray patterns
  static _rayCache = new Map();
  /**
   * Create a new voxel grid.
   * @param {number} cellSize - Size of each grid cell in world units (default 1)
   */
  constructor(cellSize = 1) {
    this.cellSize = cellSize;
    // Sparse grid storage: "x,y,z" -> Set of cuboids overlapping this cell
    this.grid = new Map();
    // Track all cuboids for quick lookup
    this.allCuboids = new Set();
    // Track bounds for stats
    this.minBounds = { x: 0, y: 0, z: 0 };
    this.maxBounds = { x: 0, y: 0, z: 0 };
  }

  /**
   * Get the grid cell key for a world position.
   * @param {number} x - World X
   * @param {number} y - World Y
   * @param {number} z - World Z
   * @returns {string} Cell key
   */
  getCellKey(x, y, z) {
    const cx = Math.floor(x / this.cellSize);
    const cy = Math.floor(y / this.cellSize);
    const cz = Math.floor(z / this.cellSize);
    return `${cx},${cy},${cz}`;
  }

  /**
   * Check if a cell is occupied.
   * @param {number} cx - Cell X
   * @param {number} cy - Cell Y
   * @param {number} cz - Cell Z
   * @returns {boolean} True if occupied
   */
  isOccupied(cx, cy, cz) {
    const cell = this.grid.get(`${cx},${cy},${cz}`);
    return cell && cell.size > 0;
  }

  /**
   * Get cuboids in a cell.
   * @param {number} cx - Cell X
   * @param {number} cy - Cell Y
   * @param {number} cz - Cell Z
   * @returns {Set<Cuboid>|null} Set of cuboids or null if empty
   */
  getCuboidsInCell(cx, cy, cz) {
    return this.grid.get(`${cx},${cy},${cz}`) || null;
  }

  /**
   * Add a cuboid to the grid.
   * Stores reference in all cells it overlaps for precise intersection testing.
   * @param {Cuboid} cuboid - The cuboid to add
   */
  addCuboid(cuboid) {
    // Calculate all cells this cuboid overlaps
    const minCx = Math.floor(cuboid.x / this.cellSize);
    const maxCx = Math.floor((cuboid.x + cuboid.width) / this.cellSize);
    const minCy = Math.floor(cuboid.y / this.cellSize);
    const maxCy = Math.floor((cuboid.y + cuboid.height) / this.cellSize);
    const minCz = Math.floor(cuboid.z / this.cellSize);
    const maxCz = Math.floor((cuboid.z + cuboid.depth) / this.cellSize);

    // Add cuboid reference to each overlapping cell
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const key = `${cx},${cy},${cz}`;
          if (!this.grid.has(key)) {
            this.grid.set(key, new Set());
          }
          this.grid.get(key).add(cuboid);
        }
      }
    }

    // Track this cuboid
    this.allCuboids.add(cuboid);

    // Update bounds
    this.minBounds.x = Math.min(this.minBounds.x, minCx);
    this.minBounds.y = Math.min(this.minBounds.y, minCy);
    this.minBounds.z = Math.min(this.minBounds.z, minCz);
    this.maxBounds.x = Math.max(this.maxBounds.x, maxCx);
    this.maxBounds.y = Math.max(this.maxBounds.y, maxCy);
    this.maxBounds.z = Math.max(this.maxBounds.z, maxCz);
  }

  /**
   * Remove a cuboid from the grid.
   * @param {Cuboid} cuboid - The cuboid to remove
   */
  removeCuboid(cuboid) {
    // Calculate all cells this cuboid overlaps
    const minCx = Math.floor(cuboid.x / this.cellSize);
    const maxCx = Math.floor((cuboid.x + cuboid.width) / this.cellSize);
    const minCy = Math.floor(cuboid.y / this.cellSize);
    const maxCy = Math.floor((cuboid.y + cuboid.height) / this.cellSize);
    const minCz = Math.floor(cuboid.z / this.cellSize);
    const maxCz = Math.floor((cuboid.z + cuboid.depth) / this.cellSize);

    // Remove cuboid reference from each cell
    for (let cx = minCx; cx <= maxCx; cx++) {
      for (let cy = minCy; cy <= maxCy; cy++) {
        for (let cz = minCz; cz <= maxCz; cz++) {
          const key = `${cx},${cy},${cz}`;
          const cell = this.grid.get(key);
          if (cell) {
            cell.delete(cuboid);
            // Clean up empty cells
            if (cell.size === 0) {
              this.grid.delete(key);
            }
          }
        }
      }
    }

    // Remove from tracking
    this.allCuboids.delete(cuboid);
  }

  /**
   * Rebuild the entire grid from a cuboid map.
   * @param {Map<string, Cuboid>} cuboids - Map of cuboid key to Cuboid
   */
  rebuild(cuboids) {
    this.clear();
    for (const cuboid of cuboids.values()) {
      this.addCuboid(cuboid);
    }
  }

  /**
   * Clear all data from the grid.
   */
  clear() {
    this.grid.clear();
    this.allCuboids.clear();
    this.minBounds = { x: 0, y: 0, z: 0 };
    this.maxBounds = { x: 0, y: 0, z: 0 };
  }

  /**
   * Ray-AABB intersection test.
   * Returns the distance to intersection, or Infinity if no hit.
   * @param {Object} origin - Ray origin {x, y, z}
   * @param {Object} dir - Normalized ray direction {x, y, z}
   * @param {Cuboid} cuboid - The cuboid to test
   * @param {number} maxDist - Maximum distance to consider
   * @returns {number} Distance to intersection, or Infinity if no hit
   */
  rayAABBIntersect(origin, dir, cuboid, maxDist) {
    // AABB bounds
    const minX = cuboid.x;
    const maxX = cuboid.x + cuboid.width;
    const minY = cuboid.y;
    const maxY = cuboid.y + cuboid.height;
    const minZ = cuboid.z;
    const maxZ = cuboid.z + cuboid.depth;

    // Compute intersection distances for each slab
    let tMin = 0;
    let tMax = maxDist;

    // X slab
    if (Math.abs(dir.x) < 1e-8) {
      // Ray parallel to X slab
      if (origin.x < minX || origin.x > maxX) return Infinity;
    } else {
      const invD = 1 / dir.x;
      let t0 = (minX - origin.x) * invD;
      let t1 = (maxX - origin.x) * invD;
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
      tMin = Math.max(tMin, t0);
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return Infinity;
    }

    // Y slab
    if (Math.abs(dir.y) < 1e-8) {
      if (origin.y < minY || origin.y > maxY) return Infinity;
    } else {
      const invD = 1 / dir.y;
      let t0 = (minY - origin.y) * invD;
      let t1 = (maxY - origin.y) * invD;
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
      tMin = Math.max(tMin, t0);
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return Infinity;
    }

    // Z slab
    if (Math.abs(dir.z) < 1e-8) {
      if (origin.z < minZ || origin.z > maxZ) return Infinity;
    } else {
      const invD = 1 / dir.z;
      let t0 = (minZ - origin.z) * invD;
      let t1 = (maxZ - origin.z) * invD;
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; }
      tMin = Math.max(tMin, t0);
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return Infinity;
    }

    // Return entry distance (must be positive)
    return tMin >= 0 ? tMin : (tMax >= 0 ? 0 : Infinity);
  }

  /**
   * Ray-AABB intersection test with hit face information.
   * Returns the distance to intersection and which face was hit.
   * @param {Object} origin - Ray origin {x, y, z}
   * @param {Object} dir - Normalized ray direction {x, y, z}
   * @param {Cuboid} cuboid - The cuboid to test
   * @param {number} maxDist - Maximum distance to consider
   * @returns {{dist: number, face: string|null}} Distance and hit face name, or {dist: Infinity, face: null}
   */
  rayAABBIntersectWithFace(origin, dir, cuboid, maxDist) {
    // AABB bounds
    const minX = cuboid.x;
    const maxX = cuboid.x + cuboid.width;
    const minY = cuboid.y;
    const maxY = cuboid.y + cuboid.height;
    const minZ = cuboid.z;
    const maxZ = cuboid.z + cuboid.depth;

    // Track which axis/side had the max tMin (entry point)
    let tMin = 0;
    let tMax = maxDist;
    let entryAxis = -1; // 0=X, 1=Y, 2=Z
    let entrySide = 0;  // -1=min side, +1=max side

    // X slab
    if (Math.abs(dir.x) < 1e-8) {
      if (origin.x < minX || origin.x > maxX) return { dist: Infinity, face: null };
    } else {
      const invD = 1 / dir.x;
      let t0 = (minX - origin.x) * invD;
      let t1 = (maxX - origin.x) * invD;
      let side = -1; // entering from min side (left)
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; side = 1; } // entering from max side (right)
      if (t0 > tMin) { tMin = t0; entryAxis = 0; entrySide = side; }
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return { dist: Infinity, face: null };
    }

    // Y slab
    if (Math.abs(dir.y) < 1e-8) {
      if (origin.y < minY || origin.y > maxY) return { dist: Infinity, face: null };
    } else {
      const invD = 1 / dir.y;
      let t0 = (minY - origin.y) * invD;
      let t1 = (maxY - origin.y) * invD;
      let side = -1; // entering from min side (bottom)
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; side = 1; } // entering from max side (top)
      if (t0 > tMin) { tMin = t0; entryAxis = 1; entrySide = side; }
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return { dist: Infinity, face: null };
    }

    // Z slab
    if (Math.abs(dir.z) < 1e-8) {
      if (origin.z < minZ || origin.z > maxZ) return { dist: Infinity, face: null };
    } else {
      const invD = 1 / dir.z;
      let t0 = (minZ - origin.z) * invD;
      let t1 = (maxZ - origin.z) * invD;
      let side = -1; // entering from min side (back)
      if (t0 > t1) { const tmp = t0; t0 = t1; t1 = tmp; side = 1; } // entering from max side (front)
      if (t0 > tMin) { tMin = t0; entryAxis = 2; entrySide = side; }
      tMax = Math.min(tMax, t1);
      if (tMin > tMax) return { dist: Infinity, face: null };
    }

    // Determine hit face based on entry axis and side
    let face = null;
    if (tMin >= 0) {
      if (entryAxis >= 0) {
        switch (entryAxis) {
          case 0: face = entrySide < 0 ? 'left' : 'right'; break;
          case 1: face = entrySide < 0 ? 'bottom' : 'top'; break;
          case 2: face = entrySide < 0 ? 'back' : 'front'; break;
        }
      } else {
        // entryAxis is -1: ray starts exactly on boundary (all t0 <= 0)
        // Determine face based on ray direction - which face would ray enter from
        const absX = Math.abs(dir.x);
        const absY = Math.abs(dir.y);
        const absZ = Math.abs(dir.z);
        if (absX >= absY && absX >= absZ) {
          face = dir.x > 0 ? 'left' : 'right';
        } else if (absY >= absZ) {
          face = dir.y > 0 ? 'bottom' : 'top';
        } else {
          face = dir.z > 0 ? 'back' : 'front';
        }
      }
    } else if (tMax >= 0) {
      // Ray starts inside - use exit face (opposite)
      tMin = 0;
      // Determine exit face based on ray direction
      const absX = Math.abs(dir.x);
      const absY = Math.abs(dir.y);
      const absZ = Math.abs(dir.z);
      if (absX >= absY && absX >= absZ) {
        face = dir.x > 0 ? 'right' : 'left';
      } else if (absY >= absZ) {
        face = dir.y > 0 ? 'top' : 'bottom';
      } else {
        face = dir.z > 0 ? 'front' : 'back';
      }
    }

    return { dist: tMin >= 0 ? tMin : (tMax >= 0 ? 0 : Infinity), face };
  }

  /**
   * Hybrid DDA ray march with precise AABB intersection.
   * Uses DDA for fast empty-space skipping, then precise intersection for accuracy.
   * @param {Object} origin - Ray origin {x, y, z} in world coordinates
   * @param {Object} dir - Normalized ray direction {x, y, z}
   * @param {number} maxDist - Maximum march distance in world units
   * @param {Cuboid} skipCuboid - Optional cuboid to skip (the source cuboid)
   * @returns {number} Distance to first hit, or maxDist if no hit
   */
  march(origin, dir, maxDist, skipCuboid = null) {
    const cellSize = this.cellSize;

    // Current cell position
    let cx = Math.floor(origin.x / cellSize);
    let cy = Math.floor(origin.y / cellSize);
    let cz = Math.floor(origin.z / cellSize);

    // Direction signs
    const stepX = dir.x >= 0 ? 1 : -1;
    const stepY = dir.y >= 0 ? 1 : -1;
    const stepZ = dir.z >= 0 ? 1 : -1;

    // Calculate tMax and tDelta for DDA
    const invDirX = dir.x !== 0 ? 1 / dir.x : Infinity;
    const invDirY = dir.y !== 0 ? 1 / dir.y : Infinity;
    const invDirZ = dir.z !== 0 ? 1 / dir.z : Infinity;

    let tMaxX = dir.x !== 0
      ? ((stepX > 0 ? (cx + 1) * cellSize : cx * cellSize) - origin.x) * invDirX
      : Infinity;
    let tMaxY = dir.y !== 0
      ? ((stepY > 0 ? (cy + 1) * cellSize : cy * cellSize) - origin.y) * invDirY
      : Infinity;
    let tMaxZ = dir.z !== 0
      ? ((stepZ > 0 ? (cz + 1) * cellSize : cz * cellSize) - origin.z) * invDirZ
      : Infinity;

    const tDeltaX = Math.abs(cellSize * invDirX);
    const tDeltaY = Math.abs(cellSize * invDirY);
    const tDeltaZ = Math.abs(cellSize * invDirZ);

    // Track closest hit
    let closestHit = maxDist;

    // Track tested cuboids to avoid duplicate tests
    const testedCuboids = new Set();
    if (skipCuboid) testedCuboids.add(skipCuboid);

    // Current distance along ray
    let t = 0;

    // March through grid
    while (t < closestHit) {
      // Get cuboids in current cell
      const key = `${cx},${cy},${cz}`;
      const cuboids = this.grid.get(key);

      if (cuboids) {
        // Test precise intersection with each cuboid in this cell
        for (const cuboid of cuboids) {
          if (testedCuboids.has(cuboid)) continue;
          testedCuboids.add(cuboid);

          const hitDist = this.rayAABBIntersect(origin, dir, cuboid, closestHit);
          if (hitDist < closestHit) {
            closestHit = hitDist;
          }
        }
      }

      // Move to next cell (step along axis with smallest tMax)
      if (tMaxX < tMaxY && tMaxX < tMaxZ) {
        t = tMaxX;
        cx += stepX;
        tMaxX += tDeltaX;
      } else if (tMaxY < tMaxZ) {
        t = tMaxY;
        cy += stepY;
        tMaxY += tDeltaY;
      } else {
        t = tMaxZ;
        cz += stepZ;
        tMaxZ += tDeltaZ;
      }
    }

    return closestHit;
  }

  /**
   * Hybrid DDA ray march with transparency support.
   * Instead of returning distance to first opaque hit, accumulates opacity
   * through transparent surfaces until fully opaque or max distance.
   * @param {Object} origin - Ray origin {x, y, z} in world coordinates
   * @param {Object} dir - Normalized ray direction {x, y, z}
   * @param {number} maxDist - Maximum march distance in world units
   * @param {Cuboid} skipCuboid - Optional cuboid to skip (the source cuboid)
   * @param {Function} getOpacity - Callback (cuboid, faceName) => opacity (0-1)
   * @returns {{dist: number, opacity: number}} Distance to first hit and accumulated opacity
   */
  marchWithTransparency(origin, dir, maxDist, skipCuboid = null, getOpacity = null) {
    const cellSize = this.cellSize;

    // Current cell position
    let cx = Math.floor(origin.x / cellSize);
    let cy = Math.floor(origin.y / cellSize);
    let cz = Math.floor(origin.z / cellSize);

    // Direction signs
    const stepX = dir.x >= 0 ? 1 : -1;
    const stepY = dir.y >= 0 ? 1 : -1;
    const stepZ = dir.z >= 0 ? 1 : -1;

    // Calculate tMax and tDelta for DDA
    const invDirX = dir.x !== 0 ? 1 / dir.x : Infinity;
    const invDirY = dir.y !== 0 ? 1 / dir.y : Infinity;
    const invDirZ = dir.z !== 0 ? 1 / dir.z : Infinity;

    let tMaxX = dir.x !== 0
      ? ((stepX > 0 ? (cx + 1) * cellSize : cx * cellSize) - origin.x) * invDirX
      : Infinity;
    let tMaxY = dir.y !== 0
      ? ((stepY > 0 ? (cy + 1) * cellSize : cy * cellSize) - origin.y) * invDirY
      : Infinity;
    let tMaxZ = dir.z !== 0
      ? ((stepZ > 0 ? (cz + 1) * cellSize : cz * cellSize) - origin.z) * invDirZ
      : Infinity;

    const tDeltaX = Math.abs(cellSize * invDirX);
    const tDeltaY = Math.abs(cellSize * invDirY);
    const tDeltaZ = Math.abs(cellSize * invDirZ);

    // Track hits for transparency accumulation
    const hits = []; // Array of {dist, cuboid, face}
    const testedCuboids = new Set();
    if (skipCuboid) testedCuboids.add(skipCuboid);

    // Current distance along ray
    let t = 0;

    // March through grid and collect all hits
    while (t < maxDist) {
      const key = `${cx},${cy},${cz}`;
      const cuboids = this.grid.get(key);

      if (cuboids) {
        for (const cuboid of cuboids) {
          if (testedCuboids.has(cuboid)) continue;
          testedCuboids.add(cuboid);

          const result = this.rayAABBIntersectWithFace(origin, dir, cuboid, maxDist);
          if (result.dist < maxDist) {
            // Record hit even if face is null (boundary case) - will be treated as opaque
            hits.push({ dist: result.dist, cuboid, face: result.face });
          }
        }
      }

      // Move to next cell
      if (tMaxX < tMaxY && tMaxX < tMaxZ) {
        t = tMaxX;
        cx += stepX;
        tMaxX += tDeltaX;
      } else if (tMaxY < tMaxZ) {
        t = tMaxY;
        cy += stepY;
        tMaxY += tDeltaY;
      } else {
        t = tMaxZ;
        cz += stepZ;
        tMaxZ += tDeltaZ;
      }
    }

    // No hits - fully transparent path
    if (hits.length === 0) {
      return { dist: maxDist, opacity: 0 };
    }

    // Sort hits by distance
    hits.sort((a, b) => a.dist - b.dist);

    // If no opacity callback, use first hit as fully opaque (backwards compatible)
    if (!getOpacity) {
      return { dist: hits[0].dist, opacity: 1 };
    }

    // Accumulate opacity through transparent surfaces
    let accumulatedOpacity = 0;
    let firstHitDist = hits[0].dist;

    for (const hit of hits) {
      // If face is null (boundary case), treat as fully opaque
      const faceOpacity = hit.face ? getOpacity(hit.cuboid, hit.face) : 1.0;

      // Calculate remaining transmittance and how much this surface blocks
      const remainingTransmittance = 1 - accumulatedOpacity;
      accumulatedOpacity += faceOpacity * remainingTransmittance;

      // If fully opaque, stop
      if (accumulatedOpacity >= 0.999) {
        accumulatedOpacity = 1;
        break;
      }
    }

    return { dist: firstHitDist, opacity: accumulatedOpacity };
  }

  /**
   * Generate hemisphere ray directions around a normal.
   * Uses Fibonacci spiral (golden angle) for well-distributed samples.
   * Cosine-weighted for physically correct AO.
   * Results are cached since only 6 discrete face normals exist.
   * @param {Object} normal - Surface normal {x, y, z}
   * @param {number} numRays - Number of rays (8-32 recommended)
   * @returns {Array<Object>} Array of ray directions {x, y, z}
   */
  getHemisphereRays(normal, numRays = 8) {
    // Round normal components for cache key (only 6 discrete normals for faces)
    const nx = Math.round(normal.x);
    const ny = Math.round(normal.y);
    const nz = Math.round(normal.z);
    const cacheKey = `${nx},${ny},${nz}|${numRays}`;

    // Check cache first
    if (AOVoxelGrid._rayCache.has(cacheKey)) {
      return AOVoxelGrid._rayCache.get(cacheKey);
    }

    const rays = [];

    // Golden angle in radians - creates optimal point distribution
    const goldenAngle = Math.PI * (3 - Math.sqrt(5));

    // Build a coordinate frame around the normal (tangent space)
    let tangent;
    if (Math.abs(normal.y) < 0.9) {
      tangent = { x: 0, y: 1, z: 0 };
    } else {
      tangent = { x: 1, y: 0, z: 0 };
    }

    // Gram-Schmidt orthogonalization
    const dot = normal.x * tangent.x + normal.y * tangent.y + normal.z * tangent.z;
    tangent = {
      x: tangent.x - dot * normal.x,
      y: tangent.y - dot * normal.y,
      z: tangent.z - dot * normal.z
    };
    const tLen = Math.sqrt(tangent.x * tangent.x + tangent.y * tangent.y + tangent.z * tangent.z);
    tangent.x /= tLen;
    tangent.y /= tLen;
    tangent.z /= tLen;

    // Cross product for bitangent
    const bitangent = {
      x: normal.y * tangent.z - normal.z * tangent.y,
      y: normal.z * tangent.x - normal.x * tangent.z,
      z: normal.x * tangent.y - normal.y * tangent.x
    };

    // Generate rays using Fibonacci hemisphere sampling
    for (let i = 0; i < numRays; i++) {
      const t = (i + 0.5) / numRays;
      const cosTheta = 1 - t;
      const sinTheta = Math.sqrt(1 - cosTheta * cosTheta);
      const phi = goldenAngle * i;
      const cosPhi = Math.cos(phi);
      const sinPhi = Math.sin(phi);

      const localX = sinTheta * cosPhi;
      const localY = cosTheta;
      const localZ = sinTheta * sinPhi;

      rays.push({
        x: localX * tangent.x + localY * normal.x + localZ * bitangent.x,
        y: localX * tangent.y + localY * normal.y + localZ * bitangent.y,
        z: localX * tangent.z + localY * normal.z + localZ * bitangent.z
      });
    }

    // Cache the result
    AOVoxelGrid._rayCache.set(cacheKey, rays);

    return rays;
  }

  /**
   * Sample ambient occlusion at a world position.
   * @param {Object} point - Sample point {x, y, z}
   * @param {Object} normal - Surface normal {x, y, z}
   * @param {number} radius - Max ray distance in world units
   * @param {number} numRays - Number of rays to cast
   * @param {number} intensity - AO intensity multiplier
   * @param {Cuboid} skipCuboid - Optional cuboid to skip (the source)
   * @returns {number} AO value 0-1 (1 = fully lit, 0 = fully occluded)
   */
  sampleAO(point, normal, radius, numRays = 8, intensity = 0.5, skipCuboid = null) {
    const rays = this.getHemisphereRays(normal, numRays);
    return this.sampleAOWithRays(point, rays, radius, intensity, skipCuboid);
  }

  /**
   * Sample ambient occlusion using pre-computed rays.
   * Use this in tight loops where rays have been pre-computed once per face.
   * @param {Object} point - Sample point {x, y, z}
   * @param {Array<Object>} rays - Pre-computed ray directions from getHemisphereRays
   * @param {number} radius - Max ray distance in world units
   * @param {number} intensity - AO intensity multiplier
   * @param {Cuboid} skipCuboid - Optional cuboid to skip (the source)
   * @returns {number} AO value 0-1 (1 = fully lit, 0 = fully occluded)
   */
  sampleAOWithRays(point, rays, radius, intensity = 0.5, skipCuboid = null) {
    let occlusion = 0;
    const numRays = rays.length;

    for (let i = 0; i < numRays; i++) {
      const dir = rays[i];
      const hitDist = this.march(point, dir, radius, skipCuboid);

      if (hitDist < radius) {
        // Weight by distance - closer hits = more occlusion
        const weight = 1 - (hitDist / radius);
        occlusion += weight * weight; // Quadratic falloff
      }
    }

    // Normalize and apply intensity
    const normalizedOcclusion = (occlusion / numRays) * intensity;

    // Return AO value (1 = fully lit, lower = more occluded)
    return Math.max(0.3, 1 - Math.min(1, normalizedOcclusion));
  }

  /**
   * Sample ambient occlusion with transparency support.
   * Rays pass through transparent surfaces with reduced occlusion contribution.
   * @param {Object} point - Sample point {x, y, z}
   * @param {Array<Object>} rays - Pre-computed ray directions from getHemisphereRays
   * @param {number} radius - Max ray distance in world units
   * @param {number} intensity - AO intensity multiplier
   * @param {Cuboid} skipCuboid - Optional cuboid to skip (the source)
   * @param {Function} getOpacity - Callback (cuboid, faceName) => opacity (0-1)
   * @returns {number} AO value 0-1 (1 = fully lit, 0 = fully occluded)
   */
  sampleAOWithTransparency(point, rays, radius, intensity = 0.5, skipCuboid = null, getOpacity = null) {
    let occlusion = 0;
    const numRays = rays.length;

    for (let i = 0; i < numRays; i++) {
      const dir = rays[i];
      const result = this.marchWithTransparency(point, dir, radius, skipCuboid, getOpacity);

      if (result.dist < radius && result.opacity > 0) {
        // Weight by distance - closer hits = more occlusion
        const distWeight = 1 - (result.dist / radius);
        // Scale by accumulated opacity through transparent surfaces
        occlusion += distWeight * distWeight * result.opacity;
      }
    }

    // Normalize and apply intensity
    const normalizedOcclusion = (occlusion / numRays) * intensity;

    // Return AO value (1 = fully lit, lower = more occluded)
    return Math.max(0.3, 1 - Math.min(1, normalizedOcclusion));
  }

  /**
   * Get statistics about the grid.
   * @returns {Object} Statistics
   */
  getStats() {
    let totalCuboidRefs = 0;
    for (const cell of this.grid.values()) {
      totalCuboidRefs += cell.size;
    }

    return {
      cellCount: this.grid.size,
      cuboidCount: this.allCuboids.size,
      totalCuboidRefs,
      avgCuboidsPerCell: this.grid.size > 0 ? (totalCuboidRefs / this.grid.size).toFixed(2) : 0,
      cellSize: this.cellSize,
      bounds: {
        min: { ...this.minBounds },
        max: { ...this.maxBounds }
      }
    };
  }
}
